// Basic
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
// Libs
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import Icon from '@/common-utils/components/renders/icon/Icon';
import Select from '@/common-utils/components/ui/select';
import { Label } from '@/common-utils/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/common-utils/components/ui/radio-group';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
import { Skeleton } from '@/common-utils/components/ui/skeleton';
// Common
import ICONS from '@/common-utils/enums/icons';
import customizationFields from '../../../../fields/customizationFields';
import isOptionChecked from '../../../../utils/isOptionChecked';
// Lang
import { nsCustomization } from '@/app/i18n/namespaces';
import CheckboxHelper from '@/common-utils/components/renders/checkbox/CheckboxHelper';

/**
 * @param root0
 * @param root0.selectedPaymentTriggers
 * @param root0.setSelectedPaymentTriggers
 * @param root0.customization
 * @param root0.isLoading
 * @param root0.isDisabled
 * @param root0.setIsValidConfig
 * @param root0.setIsScreenSaver
 * @param root0.setIsExternalTrigger
 * @param root0.checkedOptions
 * @returns {JSX} PaymentsTriggers
 */
function PaymentsTriggers({
    checkedOptions,
    selectedPaymentTriggers,
    setSelectedPaymentTriggers,
    customization,
    isLoading = false,
    isDisabled = false,
    setIsValidConfig,
    setIsScreenSaver,
    setIsExternalTrigger
}) {

    // Constants
    const DEFAULT_PORT = customization?.[customizationFields.MPF_OPTIONS.key]?.find((item) =>
        item.name === customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V3_2.key)?.value?.[customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V3_2.VALUE.PORT.key]
        || '8888';
    const DEFAULT_SPEED_RATE = customization?.[customizationFields.MPF_OPTIONS.key]?.find((item) =>
        item.name === customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V2.key)?.value?.[customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V2.VALUE.SPEED_RATE.key]
        || 9600;
    const EXTERNAL_TRIGGER_KEY = 'externalTrigger';
    const entityPrefix = 'details.functionnalities.payment_triggers';
    const modesOptions = [
        customizationFields.MPF_OPTIONS.AMOUNT_SCREEN_DISPLAY.key,
        customizationFields.MPF_OPTIONS.DONATIONS_MODE.key,
        customizationFields.MPF_OPTIONS.AUTOMATE_MODE.key
    ];
    const externalTriggersOptions = [
        customizationFields.MPF_OPTIONS.APP_TO_APP_MODE.key,
        customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V3_2.key,
        customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V2_USB.key,
        customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V2.key
    ];
    const speedRateOptions = [1200, 2400, 4800, 9600, 19200, 38400, 57600, 115200];

    // Form
    const formSchema = z.object({
        portNumber: z.coerce
            .number()
            .int()
            .min(1024, '')
            .max(65535, '')
    });
    const portForm = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange'
    });

    // State
    const [triggerMode, setTriggerMode] = useState(modesOptions[0]);
    const [selectedExternalTriggers, setSelectedExternalTriggers] = useState([]);
    const [selectedSpeedRate, setSelectedSpeedRate] = useState(DEFAULT_SPEED_RATE);

    // Helper functions
    const getDefaultModes = () => {
        return (
            customization?.[customizationFields.MPF_OPTIONS.key]?.filter(
                ({ name }) =>
                    modesOptions.includes(name) ||
                    externalTriggersOptions.includes(name)
            ) ?? [{ name: modesOptions[0], value: null }]
        );
    };

    const getDefaultTriggerMode = () => {
        const modes = customization?.[customizationFields.MPF_OPTIONS.key] || [];

        if (modes.some((m) => externalTriggersOptions.includes(m.name))) {
            return EXTERNAL_TRIGGER_KEY;
        }
        return modes.find((m) => modesOptions.includes(m.name))?.name || modesOptions[0];
    };

    const getDefaultPort = () => {
        return customization?.[customizationFields.MPF_OPTIONS.key]?.
            find((item) => item.name === customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V3_2.key)?.
            value?.[customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V3_2.VALUE.PORT.key]
            || DEFAULT_PORT;
    };

    const getDefaultSpeedRate = () => customization?.[customizationFields.MPF_OPTIONS.key]?.
        find((item) => item.name === customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V2.key)?.
        value?.[customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V2.VALUE.SPEED_RATE.key]
        || DEFAULT_SPEED_RATE;

    // Helper to initialize external triggers
    const initExternalTriggers = () => {
        const fromCustomization =
            customization?.[customizationFields.MPF_OPTIONS.key]?.filter(item =>
                externalTriggersOptions.includes(item.name)
            );

        if (fromCustomization?.length) {
            return fromCustomization;
        }

        return externalTriggersOptions.map(name => ({ name, value: null }));
    };

    // Helper to check if an array contains a Key
    const includesKey = (key, array = []) => array.some(item => item.name === key);

    // Hooks
    // initialize states from customization object ---
    useEffect(() => {
        setSelectedPaymentTriggers(getDefaultModes());
        const defaultTriggerMode = getDefaultTriggerMode();
        setTriggerMode(defaultTriggerMode);
        portForm.setValue('portNumber', getDefaultPort());
        setSelectedSpeedRate(getDefaultSpeedRate());

        if (defaultTriggerMode === EXTERNAL_TRIGGER_KEY) {
            setSelectedExternalTriggers(customization?.[customizationFields.MPF_OPTIONS.key]?.filter(
                ({ name }) =>
                    externalTriggersOptions.includes(name)
            ));
        }
    }, [customization]);

    // Set selectedPaymentTriggers
    useEffect(() => {
        let value = null;
        // If donations mode is selected, set the value to the value issued by the customization object
        // Users can't change donation value
        if (triggerMode === customizationFields.MPF_OPTIONS.DONATIONS_MODE.key) {
            value = customization?.[customizationFields.MPF_OPTIONS.key]?.find((item) => item.name === customizationFields.MPF_OPTIONS.DONATIONS_MODE.key)?.value;
        }
        setSelectedPaymentTriggers(
            triggerMode === EXTERNAL_TRIGGER_KEY
                ? selectedExternalTriggers
                : [{ name: triggerMode, value: value }]
        );
    }, [triggerMode, selectedExternalTriggers]);

    // Update external triggers options when trigger mode changes
    useEffect(() => {
        if (triggerMode !== EXTERNAL_TRIGGER_KEY) {
            setSelectedExternalTriggers([]);
            setIsExternalTrigger(false);
            portForm.reset({ portNumber: DEFAULT_PORT });
            setSelectedSpeedRate(DEFAULT_SPEED_RATE);
            return;
        }

        setIsExternalTrigger(true);

        // If no external triggers are selected, initialize them
        if (!selectedExternalTriggers.length) {
            setSelectedExternalTriggers(initExternalTriggers());
        }
    }, [triggerMode]);

    // Set Port & Speed values
    const portValue = portForm.watch('portNumber');

    useEffect(() => {
        if (triggerMode === EXTERNAL_TRIGGER_KEY) {
            setSelectedExternalTriggers(prev =>
                prev.map(item => {
                    if (item.name === customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V3_2.key) {
                        return {
                            ...item,
                            value: {
                                [customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V3_2.VALUE.PORT.key]: portValue
                            }
                        };
                    }
                    else if (item.name === customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V2.key) {
                        return {
                            ...item,
                            value: {
                                [customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V2.VALUE.SPEED_RATE.key]: selectedSpeedRate
                            }
                        };
                    }
                    return item;
                })
            );
        }
    }, [portValue, selectedSpeedRate, triggerMode]);

    // Functions
    const handleCheckExternalTriggers = (name) => {
        setSelectedExternalTriggers(prev => {
            const exists = prev.find(item => item.name === name);
            const newList = exists ? prev.filter(i => i.name !== name) : [...prev, { name, value: null }];

            // Reset port and speed rate values to default when related option is unchecked
            if (name === customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V3_2.key && exists) {
                portForm.reset({ portNumber: getDefaultPort() });
            }
            if (name === customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V2.key && exists) {
                setSelectedSpeedRate(getDefaultSpeedRate());
            }
            return newList;
        });
    };

    // Constants from updated states
    const displayWarning = triggerMode === EXTERNAL_TRIGGER_KEY && selectedExternalTriggers.length > 0
        && !isOptionChecked(checkedOptions, customizationFields.MPF_OPTIONS.BURGER_MENU_LOCK.key);
    const displayError = triggerMode === EXTERNAL_TRIGGER_KEY && selectedExternalTriggers.length === 0;

    // Hook -- More setters from props
    useEffect(() => {
        const isExternalTrigger = triggerMode === EXTERNAL_TRIGGER_KEY;
        // Set Screen saver
        setIsScreenSaver(isExternalTrigger || triggerMode === modesOptions[2]);
        // Set global validation

        if (isExternalTrigger) {
            // Fix: if port value is default, isValid is set to false by the form, so we need to set it to true in this case
            setIsValidConfig(!displayError && portValue === DEFAULT_PORT ? true : portForm.formState.isValid && selectedExternalTriggers.length > 0);
        } else {
            setIsValidConfig(!displayError);
        }
    }, [triggerMode, selectedExternalTriggers]);

    return (
        <div className='p-4 flex justify-center'>
            {isLoading &&
                (
                    <div className='flex flex-col lg:flex-row w-full gap-4 lg:gap-8'>
                        <div className='flex-1 space-y-4'>
                            {[1, 2, 3].map((i) => (
                                <div key={i} className='flex items-center gap-2'>
                                    <Skeleton className='h-4 w-64 bg-gray-200' />
                                </div>
                            ))}
                        </div>
                        <div className='flex-1 space-y-4'>
                            <div className='flex items-center gap-2'>
                                <Skeleton className='h-4 w-64 bg-gray-200' />
                            </div>
                            <div className='flex items-center gap-1'>
                                <Skeleton className='h-4 w-72 bg-gray-200' />
                            </div>
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i}>
                                    <div className='flex items-center gap-3 pl-6'>
                                        <Skeleton className='h-4 w-64 bg-gray-200' />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )
            }
            {!isLoading &&
                <RadioGroup
                    value={triggerMode}
                    onValueChange={(value) => {
                        setTriggerMode(value);
                        if (value === EXTERNAL_TRIGGER_KEY) {
                            setSelectedExternalTriggers(initExternalTriggers());
                        }
                    }}
                    className='xs:flex-col lg:flex w-full lg:gap-8'
                >
                    <div className='flex-1 basis-0 space-y-4'>
                        {modesOptions.map((option, idx) => (
                            <React.Fragment key={option}>
                                <div className='flex items-center gap-2'>
                                    <RadioGroupItem
                                        value={option}
                                        id={`id_mode_${idx + 1}`}
                                        disabled={isDisabled}
                                    />
                                    <Label htmlFor={`id_mode_${idx + 1}`}>
                                        <Entity
                                            className='text-sm cursor-pointer'
                                            entity={`${entityPrefix}.options.${option}.title`}
                                            namespace={nsCustomization}
                                        />
                                    </Label>
                                </div>
                                {includesKey(customizationFields.MPF_OPTIONS.AUTOMATE_MODE.key, selectedPaymentTriggers) && idx === 2 &&
                                    (
                                        <div className='flex items-center gap-1 text-sm text-warning'>
                                            <Icon icon={ICONS.WARNING} />
                                            <Entity
                                                entity={`${entityPrefix}.options.automateMode.warning`}
                                                namespace={nsCustomization}
                                            />
                                        </div>
                                    )
                                }
                            </React.Fragment>
                        ))}
                    </div>
                    <div className='flex-1 basis-0 space-y-4'>
                        <div className='flex items-center gap-2'>
                            <RadioGroupItem
                                disabled={isDisabled}
                                value={EXTERNAL_TRIGGER_KEY}
                                id='id_other_address'
                            />
                            <Label htmlFor='id_other_address'>
                                <Entity
                                    className='text-sm cursor-pointer'
                                    entity={`${entityPrefix}.options.externalTrigger.title`}
                                    namespace={nsCustomization}
                                />
                            </Label>
                        </div>
                        {displayWarning && !displayError &&
                            (
                                <div className='flex items-center gap-1 text-sm text-warning'>
                                    <Icon icon={ICONS.WARNING} />
                                    <Entity entity={`${entityPrefix}.options.externalTrigger.warning`} namespace={nsCustomization} />
                                </div>
                            )
                        }
                        {displayError &&
                            <div className='flex text-sm text-danger'>
                                <Entity entity={`${entityPrefix}.options.externalTrigger.error`} namespace={nsCustomization} />
                            </div>
                        }
                        {externalTriggersOptions.map((sub) => (
                            <div key={sub} >
                                <CheckboxHelper
                                    isChecked={!!selectedExternalTriggers.find(item => item.name === sub)}
                                    onCheckedChange={() => handleCheckExternalTriggers(sub)}
                                    isDisabled={triggerMode !== EXTERNAL_TRIGGER_KEY || isDisabled}
                                    entity={`${entityPrefix}.options.externalTrigger.${sub}`}
                                    namespace={nsCustomization}
                                    className='sm:pl-6'
                                />
                                {sub === customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V3_2.key
                                    && includesKey(customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V3_2.key, selectedExternalTriggers) &&
                                    (
                                        <Form {...portForm} >
                                            <div className='flex items-center space-x-2 px-6 py-2'>
                                                <FormLabel htmlFor='portNumber' className='block text-sm font-medium text-gray-700'>
                                                    <Entity entity={`${entityPrefix}.keys.port`} namespace={nsCustomization} />
                                                </FormLabel>
                                                <FormField
                                                    name='portNumber'
                                                    control={portForm.control}
                                                    render={({ field }) => (
                                                        <FormItem className=''>
                                                            <FormControl>
                                                                <Input
                                                                    className='w-20 h-8'
                                                                    {...field}
                                                                    type='number'
                                                                    autoComplete='off'
                                                                    min={1024}
                                                                    max={65535}
                                                                    disabled={isDisabled || triggerMode !== EXTERNAL_TRIGGER_KEY}
                                                                />
                                                            </FormControl>
                                                        </FormItem>
                                                    )}
                                                />
                                            </div>
                                            {!portForm.formState.isValid &&
                                                <div className='flex px-6'>
                                                    <Entity
                                                        className='text-sm text-danger'
                                                        entity={`${entityPrefix}.errors.port_input`}
                                                        namespace={nsCustomization}
                                                    />
                                                </div>
                                            }
                                        </Form>
                                    )
                                }
                                {sub === customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V2.key
                                    && includesKey(customizationFields.MPF_OPTIONS.CONCERT_PROTOCOL_V2.key, selectedExternalTriggers) &&
                                    (
                                        <div className='flex flex-col items-center gap-3 lg:flex-row lg:items-center lg:justify-start pl-6 py-2'>
                                            <Entity
                                                className='text-gray-600 text-sm block'
                                                entity={`${entityPrefix}.keys.exchange_speed`}
                                                namespace={nsCustomization}
                                            />
                                            <Select
                                                className='max-w-36'
                                                placeholder='--'
                                                value={selectedSpeedRate}
                                                options={speedRateOptions.map((item) => ({ value: item, name: `${item} bps` }))}
                                                onChange={(value) => setSelectedSpeedRate(value)}
                                                namespace={nsCustomization}
                                                disabled={isDisabled || triggerMode !== EXTERNAL_TRIGGER_KEY}
                                            />
                                        </div>
                                    )
                                }
                            </div>
                        ))}
                    </div>
                </RadioGroup>}
        </div>
    );
}

PaymentsTriggers.propTypes = {
    checkedOptions: PropTypes.array,
    selectedPaymentTriggers: PropTypes.array,
    setSelectedPaymentTriggers: PropTypes.func,
    customization: PropTypes.object,
    isLoading: PropTypes.bool,
    isDisabled: PropTypes.bool,
    setIsValidConfig: PropTypes.func,
    setIsScreenSaver: PropTypes.func,
    setIsExternalTrigger: PropTypes.func
};
export default PaymentsTriggers;